<template>
  <b-modal
    ref="refModal"
    id="taxi-vehicle-modal"
    title="Taxi Vehicle"
    :ok-title="(vehicleData.id) ? 'Update' : 'Add'"
    no-close-on-backdrop
    cancel-variant="outline-secondary"
    @ok="validateForm"
  >
    <validation-observer ref="newTaxiVehicleForm">
      <b-form
        id="add-new-taxi-vehicle"
        class="p-2"
      >
        <validation-provider
          #default="{ errors }"
          name="Manufacturer"
          rules="required"
        >
          <b-form-group
            label="Manufacturer"
            label-for="manufacturer"
          >
            <b-form-input
              id="manufacturer"
              v-model="vehicleData.manufacturer"
              placeholder="Manufacturer"
              :state="errors.length > 0 ? false : null"
              name="manufacturer"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="Model"
          rules="required"
        >
          <b-form-group
            label="Model"
            label-for="model"
          >
            <b-form-input
              id="model"
              v-model="vehicleData.model"
              placeholder="Model"
              :state="errors.length > 0 ? false : null"
              name="model"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-form-group
          label="Year"
          label-for="year"
        >
          <b-form-input
            id="year"
            v-model="vehicleData.year"
            type="number"
            placeholder="Year"
            name="year"
          />
        </b-form-group>

        <validation-provider
          #default="{ errors }"
          name="Register Number"
          rules="required"
        >
          <b-form-group
            label="Register Number"
            label-for="register-number"
          >
            <b-form-input
              id="register-number"
              v-model="vehicleData.regNum"
              type="text"
              placeholder="Register Number"
              :state="errors.length > 0 ? false : null"
              name="register-number"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="Number of Seats"
          rules="required"
        >
          <b-form-group
            label="Number of Seats"
            label-for="number-seats"
          >
            <b-form-input
              id="number-seats"
              v-model="vehicleData.numSeats"
              type="number"
              placeholder="Number of Seats"
              :state="errors.length > 0 ? false : null"
              name="number-seats"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="Wheelchair Friendly"
          rules="required"
        >

          <b-form-group
            label="Wheelchair Friendly"
            label-for="wheelchair-friendly"
          >
            <v-select
              v-model="vehicleData.wheelchairFriendly"
              :options="[{ label: 'Yes', value: true }, { label: 'No', value: false }]"
              label="label"
              :reduce="(options) => options.value"
              input-id="wheelchair-friendly"
            >
              <template #option="{ label, value }">
                <div
                  class="rounded-circle d-inline-block mr-50"
                  :class="(value) ? 'bg-success' : 'bg-secondary'"
                  style="height: 10px; width: 10px"
                />
                <span> {{ label }}</span>
              </template>

              <template #selected-option="{ label, value }">
                <div
                  class="rounded-circle d-inline-block mr-50"
                  :class="(value) ? 'bg-success' : 'bg-secondary'"
                  style="height: 10px; width: 10px"
                />
                <span> {{ label }}</span>
              </template>
            </v-select>

            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="Vehicle Type"
          rules="required"
        >

          <b-form-group
            label="Vehicle Type"
            label-for="vehicle-type"
          >
            <v-select
              v-model="vehicleData.isMainVehicle"
              :options="[{ label: 'Main', value: true }, { label: 'Secondary', value: false }]"
              label="label"
              :reduce="(options) => options.value"
              input-id="vehicle-type"
            >
              <template #option="{ label, value }">
                <div
                  class="rounded-circle d-inline-block mr-50"
                  :class="(value) ? 'bg-primary' : 'bg-warning'"
                  style="height: 10px; width: 10px"
                />
                <span> {{ label }}</span>
              </template>

              <template #selected-option="{ label, value }">
                <div
                  class="rounded-circle d-inline-block mr-50"
                  :class="(value) ? 'bg-primary' : 'bg-warning'"
                  style="height: 10px; width: 10px"
                />
                <span> {{ label }}</span>
              </template>
            </v-select>

            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import { BButton, BForm, BFormGroup, BFormInput } from 'bootstrap-vue';
import { ref, watch } from "@vue/composition-api";
import { required } from '@validations';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import router from '@/router';
import useTaxiVehicle from '../composables/useTaxiVehicle';
import vSelect from 'vue-select';

export default {
  props: {
    vehicle: {
      type: Object
    }
  },
  setup(props, ctx) {
    const { createVehicle, editVehicle } = useTaxiVehicle();

    const newTaxiVehicleForm = ref(null);
    const refModal = ref(null);
    const vehicleData = ref({ ...props.vehicle });

    watch(() => props.vehicle, (value, _) => { vehicleData.value = value; });

    const validateForm = (modalEvt) => {
      modalEvt.preventDefault();

      newTaxiVehicleForm.value.validate().then(async (valid) => {
        if (!valid) return;

        if (vehicleData.value.id === null) {
          const vehicle = { ...vehicleData.value, taxiId: router.currentRoute.params.id };
          const response = await createVehicle(vehicle);

          refModal.value.hide();

          if (response !== null) {
            vehicleData.value = { ...response };
            ctx.emit('form-submitted', { ...response });
          }

        } else {
          const response = await editVehicle(vehicleData.value);

          refModal.value.hide();

          (response !== null) && ctx.emit('form-submitted', { ...vehicleData.value });
        }
      });
    };

    return {
      // Reactive
      newTaxiVehicleForm,
      refModal,
      vehicleData,

      // Method
      validateForm,

      // Validator
      required
    };
  },
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-taxi-vehicle {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>